import { FieldArray, Form, Formik, useField, useFormikContext } from "formik";
import { Plus, Trash2 } from "lucide-react";
import * as Yup from "yup";
import { yearRange } from "../validation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Client } from "@/features/clients/types";
import type { FormSubmission, GenerateFor } from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox, FormikSelect } from "./formik-controls";

// --- Form 2848A (Alabama POA) editable payload -------------------------------
// snake_case keys mirror `apps/forms/mappings/form_2848a.py` `build_defaults`
// exactly (passed verbatim, no snake→camel). Rep contact identity and taxpayer
// name/SSN/EIN/phone are filled server-side, not typed here.

/** Alabama collapses the federal g/h/i/k/r designations to "g"; the form offers a–g. */
export type AlDesignation = "a" | "b" | "c" | "d" | "e" | "f" | "g" | "";

const AL_DESIGNATION_OPTIONS: { value: Exclude<AlDesignation, "">; label: string }[] = [
  { value: "a", label: "a — Attorney" },
  { value: "b", label: "b — Certified Public Accountant" },
  { value: "c", label: "c — Enrolled Agent" },
  { value: "d", label: "d — Officer" },
  { value: "e", label: "e — Full-Time Employee" },
  { value: "f", label: "f — Family Member" },
  { value: "g", label: "g — Other" },
];

export interface Form2848aRepresentative {
  /** The backing Practitioner id (contact details are looked up from it server-side). */
  practitioner: string;
  active: boolean;
  name: string;
  designation: AlDesignation;
  /** Alabama jurisdiction / license string (licensing + license #). */
  al_jurisdiction: string;
  /** Signature date (MM/DD/YYYY). */
  date: string;
}

export interface Form2848aMatter {
  tax_type: string;
  tax_form: string;
  tax_years: string;
}

export interface Form2848aData {
  generate_for: GenerateFor;
  representatives: Form2848aRepresentative[];
  matters: Form2848aMatter[];
  act_disclose: boolean;
  act_substitute: boolean;
  act_sign: boolean;
  dont_revoke: boolean;
  exceptions: string;
}

/** The Alabama template holds three representatives and three tax-matter rows. */
export const MAX_2848A_REPS = 3;
export const MAX_2848A_MATTERS = 3;
const EXCEPTIONS_MAX = 250;

const EMPTY_MATTER: Form2848aMatter = { tax_type: "", tax_form: "", tax_years: "" };

const GENERATE_FOR_OPTIONS: { value: GenerateFor; label: string }[] = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
  { value: "jointly", label: "Jointly" },
];

const ACTS: { name: keyof Form2848aData; label: string }[] = [
  { name: "act_disclose", label: "Disclose tax information to third parties" },
  { name: "act_substitute", label: "Substitute or add representative(s)" },
  { name: "act_sign", label: "Sign a return" },
];

const schema = Yup.object({
  generate_for: Yup.string().oneOf(["taxpayer", "spouse", "jointly"]).required(),
  matters: Yup.array()
    .of(
      Yup.object({
        tax_type: Yup.string().max(60, "Too long"),
        tax_form: Yup.string().max(40, "Too long"),
        tax_years: yearRange,
      }),
    )
    .max(MAX_2848A_MATTERS),
  exceptions: Yup.string().max(EXCEPTIONS_MAX, `Keep it under ${EXCEPTIONS_MAX} characters`),
});

function toInitialValues(data: Record<string, unknown>): Form2848aData {
  const d = data as Partial<Form2848aData>;
  return {
    generate_for: d.generate_for ?? "taxpayer",
    representatives: d.representatives ?? [],
    matters:
      d.matters && d.matters.length > 0 ? d.matters : [{ ...EMPTY_MATTER }],
    act_disclose: d.act_disclose ?? false,
    act_substitute: d.act_substitute ?? false,
    act_sign: d.act_sign ?? false,
    dont_revoke: d.dont_revoke ?? false,
    exceptions: d.exceptions ?? "",
  };
}

function GenerateForField() {
  const [field, , helpers] = useField<GenerateFor>("generate_for");
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as GenerateFor)}
    >
      {GENERATE_FOR_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`gf-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`gf-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

/**
 * "Power of Attorney Given To" — the client's assigned representatives, capped at
 * the Alabama template's three. The user toggles which are active and edits each
 * one's designation (a–g), Alabama jurisdiction/license string, and signature
 * date; contact details come from the practitioner record server-side.
 */
function RepresentativesTable() {
  const { values } = useFormikContext<Form2848aData>();
  const reps = values.representatives;

  if (reps.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No representatives are assigned to this client. Add them on the client's
        profile, then reopen this form.
      </p>
    );
  }

  return (
    <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Active</TableHead>
            <TableHead>Rep Name</TableHead>
            <TableHead className="w-44">Designation</TableHead>
            <TableHead className="w-44">Jurisdiction / License</TableHead>
            <TableHead className="w-36">Signature Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reps.map((rep, i) => (
            <TableRow key={rep.practitioner || i}>
              <TableCell>
                <FormikCheckbox
                  name={`representatives.${i}.active`}
                  aria-label={`Active ${rep.name}`}
                />
              </TableCell>
              <TableCell className="font-medium">{rep.name}</TableCell>
              <TableCell>
                <FormikSelect
                  name={`representatives.${i}.designation`}
                  options={AL_DESIGNATION_OPTIONS}
                  placeholder="Select…"
                  aria-label={`Designation for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCell
                  name={`representatives.${i}.al_jurisdiction`}
                  aria-label={`Jurisdiction for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCell
                  name={`representatives.${i}.date`}
                  placeholder="MM/DD/YYYY"
                  aria-label={`Signature date for ${rep.name}`}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

/** "Tax Matters" — Type of Tax / Tax Form / Year(s) rows (capped at three). */
function MattersTable() {
  const { values } = useFormikContext<Form2848aData>();
  const matters = values.matters;

  return (
    <FieldArray name="matters">
      {(helpers) => (
        <div className="flex flex-col gap-3">
          <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type of Tax</TableHead>
                  <TableHead className="w-36">Tax Form Number</TableHead>
                  <TableHead className="w-44">Year(s) or Period(s)</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {matters.map((_matter, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_type`}
                        placeholder="e.g. Income Tax"
                        aria-label={`Type of tax for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_form`}
                        placeholder="e.g. 40"
                        aria-label={`Tax form for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_years`}
                        placeholder="e.g. 2021, 2022"
                        aria-label={`Years for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove matter ${i + 1}`}
                        disabled={matters.length === 1}
                        onClick={() => helpers.remove(i)}
                      >
                        <Trash2 className="text-muted-foreground size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={matters.length >= MAX_2848A_MATTERS}
              onClick={() => helpers.push({ ...EMPTY_MATTER })}
            >
              <Plus /> Add tax matter
            </Button>
            {matters.length >= MAX_2848A_MATTERS && (
              <span className="text-muted-foreground ml-3 text-xs">
                Form 2848A fits up to {MAX_2848A_MATTERS} matters.
              </span>
            )}
          </div>
        </div>
      )}
    </FieldArray>
  );
}

function ExceptionsField() {
  const [field] = useField("exceptions");
  return (
    <div className="grid gap-2">
      <Label htmlFor="exceptions">Specific acts not authorized (exceptions)</Label>
      <Textarea id="exceptions" rows={3} maxLength={EXCEPTIONS_MAX} {...field} />
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Alabama Form 2848A (Power of Attorney) — prefilled from the client +
 * its practitioners. Alabama deltas vs. the federal 2848: ≤3 reps each with a
 * per-rep designation (a–g), jurisdiction/license string, and signature date;
 * three tax-matter rows; three independent "acts authorized" flags; and one
 * exceptions free-text field.
 */
export function Form2848aEditor({ client, submission, onSubmissionChange }: Props) {
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-6 p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-foreground font-semibold">Generate for</span>
              <GenerateForField />
            </div>
            <div className="text-muted-foreground text-sm">
              <p className="text-foreground font-medium">{client.name}</p>
              {address && <p>{address}</p>}
            </div>
          </div>
        </Card>

        <section className="flex flex-col gap-4">
          <SectionHeading>Power of Attorney Given To</SectionHeading>
          <RepresentativesTable />
        </section>

        <section className="flex flex-col gap-4">
          <SectionHeading>Tax Matters</SectionHeading>
          <MattersTable />
        </section>

        <Card className="gap-4 p-6">
          <SectionHeading>Acts Authorized</SectionHeading>
          <div className="grid gap-3 sm:grid-cols-2">
            {ACTS.map((act) => (
              <FormikCheckbox key={act.name} name={act.name} label={act.label} />
            ))}
            <FormikCheckbox
              name="dont_revoke"
              label="Do not revoke prior Powers of Attorney"
            />
          </div>
          <ExceptionsField />
        </Card>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
